import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../Sidebar/AdminSidebar';
import AdminNavbar from '../../Navbars/AdminNavbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../../apis/superAdminApi';
import { Oval } from 'react-loader-spinner';
import {
    AlertTriangle, Plus, Save, Trash2, MapPin, Layers, X, Info, Pencil, Building2,
    Package, Scale, Image as ImageIcon, Upload, CornerUpLeft,
} from 'lucide-react';

/** Amounts render in the client's own currency — agreements are not all in naira. */
const formatMoney = (amount: any, symbol = '$') => {
    const num = Number(amount);
    const body = isNaN(num) ? '0.00' : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${symbol}${body}`;
};

/**
 * Rates carry 4 decimal places, but most are round. This prints $0.55 rather than
 * $0.5500 and only shows the extra digits when the rate genuinely has them, so the
 * precision is available without making every ordinary rate look noisy.
 */
const formatRate = (amount: any, symbol = '$') => {
    const num = Number(amount);
    if (isNaN(num)) return `${symbol}0.00`;
    const rounded4 = Math.round(num * 10000) / 10000;
    const rounded2 = Math.round(num * 100) / 100;
    if (rounded4 === rounded2) return formatMoney(num, symbol);
    return `${symbol}${rounded4.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
};

const emptyChargeForm = {
    id: null as any,
    name: '',
    charge_type: 'per_kg',
    rate: '',
    // Range only: base_amount covers the docket up to base_weight_kg, then the rate
    // is charged per additional_block_kg beyond it.
    base_amount: '',
    base_weight_kg: '',
    additional_block_kg: '1',
    description: '',
};

const emptyZoneForm = {
    name: '',
    description: '',
    base_amount: '',
    base_weight_kg: '',
    additional_block_kg: '1',
    additional_per_kg: '',
};

const trimKg = (n: number) => String(Number(n.toFixed(2)));

/**
 * Mirrors BillingZone::priceFor() on the backend so the worked example under each
 * zone shows exactly what the invoice will charge. Part of a block is a whole block.
 */
const priceFor = (weight: number, base: number, limit: number, block: number, amount: number) => {
    const b = block > 0 ? block : 1;
    const extra = Math.max(0, weight - limit);
    const blocks = Math.ceil(Number((extra / b).toFixed(6)));
    return { blocks, total: base + blocks * amount };
};

const emptyClientForm = {
    id: null as any,
    name: '',
    sender_name_match: '',
    match_type: 'sender_name',
    currency: 'USD',
    vat_enabled: false,
    vat_rate: '7.5',
    is_active: true,
    notes: '',
};

/**
 * One return rate per client, so this is a single form rather than a list. Defaults
 * to range because that is the shape the agreements actually use — a base covering
 * the first band, then a price per further block.
 */
const emptyReturnForm = {
    charge_type: 'range',
    rate: '',
    base_amount: '',
    base_weight_kg: '',
    additional_block_kg: '0.5',
    description: '',
    is_active: true,
};

/** Mirrors InvoiceCompanyService::MAX_LOGO_KB — rejected here so the file never leaves the browser. */
const MAX_LOGO_KB = 512;

const emptyCompanyForm = {
    id: null as any,
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    is_active: true,
};

function BillingZoneSettings() {
    const [loader, setLoader] = useState<boolean>(true);
    const [saving, setSaving] = useState<string>('');

    const [clients, setClients] = useState<any[]>([]);
    const [clientId, setClientId] = useState<any>('');
    const [config, setConfig] = useState<any>(null);

    // Canonical state list comes from the backend so this page cannot drift the way
    // ManageRates.tsx's hardcoded copy did (it still lists Abuja and FCT separately).
    const [allStates, setAllStates] = useState<string[]>([]);

    const [zoneForm, setZoneForm] = useState<any>(emptyZoneForm);
    const [showZoneForm, setShowZoneForm] = useState<boolean>(false);
    const [rateEdits, setRateEdits] = useState<any>({});
    const [stateModalZone, setStateModalZone] = useState<any>(null);

    // Docket-level charges — clearance and the like. One form, reused for add and edit.
    const [chargeForm, setChargeForm] = useState<any>({ ...emptyChargeForm });
    const [showChargeForm, setShowChargeForm] = useState<boolean>(false);
    const [stateSelection, setStateSelection] = useState<string[]>([]);

    const [clientForm, setClientForm] = useState<any>(null);

    // Trading names invoices can be issued under. Not per-client: any company can be
    // picked for any invoice, so this list is loaded once and stands on its own.
    // The client's single return rate. Null until loaded; `null` inside the payload
    // means the client has none set yet, which is different from having one at zero.
    const [returnRate, setReturnRate] = useState<any>(null);
    const [returnForm, setReturnForm] = useState<any>({ ...emptyReturnForm });

    const [companies, setCompanies] = useState<any[]>([]);
    const [companyForm, setCompanyForm] = useState<any>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    // Shown before the upload happens, so the operator sees what they picked rather
    // than having to save to find out.
    const [logoPreview, setLogoPreview] = useState<string>('');

    const symbol: string = config?.currency_symbol || '$';
    const currencies: any = config?.currencies || { USD: '$', NGN: '₦', GBP: '£', EUR: '€' };

    const loadClients = useCallback(async () => {
        try {
            const [clientsRes, statesRes] = await Promise.all([
                SuperAdminApis.getBillingClients(),
                SuperAdminApis.getBillingStates(),
            ]);

            if (clientsRes?.success) {
                setClients(clientsRes.data || []);
                if (!clientId && clientsRes.data?.length) {
                    setClientId(clientsRes.data[0].id);
                }
            }

            if (statesRes?.success) setAllStates(statesRes.data || []);
        } catch (e) {
            toast.error('Could not load billing clients');
        } finally {
            setLoader(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadConfig = useCallback(async (id: any) => {
        if (!id) return;
        setLoader(true);
        try {
            const res = await SuperAdminApis.getBillingConfiguration(id);
            if (res?.success) {
                setConfig(res.data);
                const edits: any = {};
                (res.data.zones || []).forEach((z: any) => {
                    edits[z.id] = {
                        base_amount: z.base_amount ?? 0,
                        base_weight_kg: z.base_weight_kg ?? 0,
                        additional_block_kg: z.additional_block_kg ?? 1,
                        additional_per_kg: z.additional_per_kg ?? 0,
                    };
                });
                setRateEdits(edits);
            } else {
                toast.warn(res?.message || 'Could not load configuration');
            }
        } catch (e) {
            toast.error('Could not load configuration');
        } finally {
            setLoader(false);
        }
    }, []);

    useEffect(() => { loadClients(); }, [loadClients]);
    useEffect(() => { if (clientId) loadConfig(clientId); }, [clientId, loadConfig]);

    /**
     * Delete a client.
     *
     * The server refuses once the client has invoices and says why, so the message
     * is surfaced rather than replaced with a generic failure — "cannot be deleted"
     * is not useful without the reason and the alternative.
     */
    const deleteClient = async () => {
        const client = config?.client;
        if (!client) return;

        const zones = config?.zones?.length ?? 0;
        const charges = config?.charges?.length ?? 0;

        if (!window.confirm(
            `Delete ${client.name}?\n\n`
            + `This also removes its ${zones} zone(s), their state assignments and ${charges} additional charge(s).\n\n`
            + `Invoices are never touched — if this client has any, the delete is refused and you can switch it off instead.`
        )) return;

        setSaving('delete-client');
        try {
            const res = await SuperAdminApis.deleteBillingClient(client.id);

            if (res?.success) {
                toast.success(res.message);
                setClientId('');
                setConfig(null);
                loadClients();
            } else {
                // Long, because it explains the alternative.
                toast.warn(res?.message || 'Could not delete this client', { autoClose: 12000 });
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not delete this client', { autoClose: 12000 });
        } finally {
            setSaving('');
        }
    };

    const openClientForm = (client: any = null) => {
        setClientForm(client
            ? {
                id: client.id,
                name: client.name,
                sender_name_match: client.sender_name_match,
                match_type: client.match_type || 'sender_name',
                currency: client.currency || 'USD',
                vat_enabled: !!client.vat_enabled,
                vat_rate: String(client.vat_rate ?? '7.5'),
                is_active: !!client.is_active,
                notes: client.notes || '',
            }
            : { ...emptyClientForm });
    };

    const saveClient = async () => {
        if (!clientForm.name.trim() || !clientForm.sender_name_match.trim()) {
            toast.warn('Client name and sender match are both required');
            return;
        }

        setSaving('client');
        try {
            const payload = {
                name: clientForm.name.trim(),
                sender_name_match: clientForm.sender_name_match.trim(),
                match_type: clientForm.match_type,
                currency: clientForm.currency,
                vat_enabled: clientForm.vat_enabled,
                vat_rate: Number(clientForm.vat_rate) || 0,
                is_active: clientForm.is_active,
                notes: clientForm.notes,
            };

            const res = clientForm.id
                ? await SuperAdminApis.updateBillingClient(clientForm.id, payload)
                : await SuperAdminApis.createBillingClient(payload);

            if (res?.success) {
                toast.success(clientForm.id ? 'Client updated' : 'Client created');
                const newId = res.data?.id;
                setClientForm(null);
                await loadClients();
                if (!clientForm.id && newId) {
                    setClientId(newId);
                } else {
                    loadConfig(clientId);
                }
            } else {
                // The backend rejects two clients sharing a sender match, which
                // would double-bill the same shipments — surface that verbatim.
                toast.warn(res?.message || 'Could not save client');
            }
        } catch (e) {
            toast.error('Could not save client');
        } finally {
            setSaving('');
        }
    };

    // ── Return rate ──────────────────────────────────────────

    const loadReturnRate = useCallback(async (id: any) => {
        if (!id) return;
        try {
            const res = await SuperAdminApis.getReturnRate(id);
            if (res?.success) {
                const rate = res.data?.rate || null;
                setReturnRate(rate);
                setReturnForm(rate
                    ? {
                        charge_type: rate.charge_type,
                        rate: String(rate.rate ?? ''),
                        base_amount: String(rate.base_amount ?? ''),
                        base_weight_kg: String(rate.base_weight_kg ?? ''),
                        additional_block_kg: String(rate.additional_block_kg ?? '1'),
                        description: rate.description || '',
                        is_active: !!rate.is_active,
                    }
                    : { ...emptyReturnForm });
            }
        } catch (e) {
            toast.error('Could not load the return rate');
        }
    }, []);

    useEffect(() => { if (clientId) loadReturnRate(clientId); }, [clientId, loadReturnRate]);

    const saveReturnRate = async () => {
        setSaving('return-rate');
        try {
            const res = await SuperAdminApis.saveReturnRate(clientId, {
                charge_type: returnForm.charge_type,
                rate: Number(returnForm.rate) || 0,
                base_amount: Number(returnForm.base_amount) || 0,
                base_weight_kg: Number(returnForm.base_weight_kg) || 0,
                additional_block_kg: Number(returnForm.additional_block_kg) || 1,
                description: returnForm.description,
                is_active: returnForm.is_active,
            });

            if (res?.success) {
                toast.success(res.message);
                loadReturnRate(clientId);
            } else {
                toast.warn(res?.message || 'Could not save the return rate');
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not save the return rate');
        } finally {
            setSaving('');
        }
    };

    // ── Invoice companies ────────────────────────────────────

    const loadCompanies = useCallback(async () => {
        try {
            const res = await SuperAdminApis.getInvoiceCompanies();
            if (res?.success) setCompanies(res.data || []);
        } catch (e) {
            toast.error('Could not load invoice companies');
        }
    }, []);

    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    const openCompanyForm = (company: any = null) => {
        setLogoFile(null);
        setLogoPreview(company?.logo || '');
        setCompanyForm(company
            ? {
                id: company.id,
                name: company.name || '',
                address: company.address || '',
                phone: company.phone || '',
                email: company.email || '',
                website: company.website || '',
                is_active: !!company.is_active,
            }
            : { ...emptyCompanyForm });
    };

    const pickLogo = (file: File | null) => {
        if (!file) return;

        // The backend enforces both of these; catching them here saves an upload that
        // is going to be refused anyway, and says so in the same words.
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
            toast.warn('The logo must be a PNG or JPEG — those are the two the invoice PDF renders reliably.');
            return;
        }
        if (file.size > MAX_LOGO_KB * 1024) {
            toast.warn(`That logo is ${Math.round(file.size / 1024)}KB. The limit is ${MAX_LOGO_KB}KB — it is embedded into every PDF.`);
            return;
        }

        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = () => setLogoPreview(String(reader.result || ''));
        reader.readAsDataURL(file);
    };

    const saveCompany = async () => {
        if (!companyForm?.name.trim()) {
            toast.warn('Give the company a name');
            return;
        }

        setSaving('company');
        try {
            const res = await SuperAdminApis.saveInvoiceCompany(companyForm.id, {
                name: companyForm.name.trim(),
                address: companyForm.address,
                phone: companyForm.phone,
                email: companyForm.email,
                website: companyForm.website,
                is_active: companyForm.is_active ? 1 : 0,
            }, logoFile);

            if (res?.success) {
                toast.success(res.message || 'Company saved');
                setCompanyForm(null);
                setLogoFile(null);
                setLogoPreview('');
                loadCompanies();
            } else {
                toast.warn(res?.message || 'Could not save company');
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not save company');
        } finally {
            setSaving('');
        }
    };

    /**
     * Switch a company on or off without opening the form.
     *
     * Off is the alternative to deleting one that is already on invoices: it stops
     * appearing when generating new ones and the documents it has already issued are
     * untouched.
     */
    const toggleCompany = async (company: any) => {
        setSaving(`company-${company.id}`);
        try {
            const res = await SuperAdminApis.saveInvoiceCompany(company.id, { is_active: company.is_active ? 0 : 1 }, null);
            if (res?.success) {
                toast.success(`${company.name} ${company.is_active ? 'switched off' : 'switched on'}`);
                loadCompanies();
            } else {
                toast.warn(res?.message || 'Could not update company');
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not update company');
        } finally {
            setSaving('');
        }
    };

    const deleteCompany = async (company: any) => {
        if (!window.confirm(
            `Delete ${company.name}?\n\n`
            + 'Invoices already issued under this name resolve their letterhead from it, so if any exist '
            + 'the delete is refused and you can switch it off instead.'
        )) return;

        setSaving(`company-${company.id}`);
        try {
            const res = await SuperAdminApis.deleteInvoiceCompany(company.id);
            if (res?.success) {
                toast.success(res.message);
                loadCompanies();
            } else {
                // Long, because it explains the alternative.
                toast.warn(res?.message || 'Could not delete this company', { autoClose: 12000 });
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not delete this company', { autoClose: 12000 });
        } finally {
            setSaving('');
        }
    };

    const saveRate = async (zone: any) => {
        const edit = rateEdits[zone.id] || {};
        setSaving(`rate-${zone.id}`);
        try {
            const res = await SuperAdminApis.updateBillingZone(zone.id, {
                base_amount: Number(edit.base_amount) || 0,
                base_weight_kg: Number(edit.base_weight_kg) || 0,
                additional_block_kg: Number(edit.additional_block_kg) || 1,
                additional_per_kg: Number(edit.additional_per_kg) || 0,
            });
            if (res?.success) {
                toast.success(`${zone.name} rate saved`);
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not save rate');
            }
        } catch (e) {
            toast.error('Could not save rate');
        } finally {
            setSaving('');
        }
    };

    const createZone = async () => {
        if (!zoneForm.name.trim()) {
            toast.warn('Give the zone a name');
            return;
        }
        setSaving('zone-new');
        try {
            const res = await SuperAdminApis.createBillingZone(clientId, {
                name: zoneForm.name.trim(),
                description: zoneForm.description,
                base_amount: Number(zoneForm.base_amount) || 0,
                base_weight_kg: Number(zoneForm.base_weight_kg) || 0,
                additional_block_kg: Number(zoneForm.additional_block_kg) || 1,
                additional_per_kg: Number(zoneForm.additional_per_kg) || 0,
            });
            if (res?.success) {
                toast.success('Zone created');
                setZoneForm(emptyZoneForm);
                setShowZoneForm(false);
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not create zone');
            }
        } catch (e) {
            toast.error('Could not create zone');
        } finally {
            setSaving('');
        }
    };

    const deleteZone = async (zone: any) => {
        if (!window.confirm(`Delete ${zone.name}? Its ${zone.state_count} state(s) will become unassigned. Invoices already generated are not affected.`)) return;
        setSaving(`del-${zone.id}`);
        try {
            const res = await SuperAdminApis.deleteBillingZone(zone.id);
            if (res?.success) {
                toast.success('Zone deleted');
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not delete zone');
            }
        } catch (e) {
            toast.error('Could not delete zone');
        } finally {
            setSaving('');
        }
    };

    // ── Docket-level charges ─────────────────────────────────────────────

    const saveCharge = async () => {
        if (!chargeForm.name.trim()) {
            toast.warn('Give the charge a name, e.g. Clearance');
            return;
        }
        if (chargeForm.rate === '' || isNaN(Number(chargeForm.rate))) {
            toast.warn('Enter the rate');
            return;
        }

        setSaving('charge');
        try {
            const payload = {
                name: chargeForm.name.trim(),
                charge_type: chargeForm.charge_type,
                rate: Number(chargeForm.rate),
                base_amount: Number(chargeForm.base_amount || 0),
                base_weight_kg: Number(chargeForm.base_weight_kg || 0),
                additional_block_kg: Number(chargeForm.additional_block_kg || 1),
                description: chargeForm.description || null,
            };

            const res = chargeForm.id
                ? await SuperAdminApis.updateBillingCharge(chargeForm.id, payload)
                : await SuperAdminApis.createBillingCharge(clientId, payload);

            if (res?.success) {
                toast.success(res?.message || 'Charge saved');
                setChargeForm({ ...emptyChargeForm });
                setShowChargeForm(false);
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not save charge');
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not save charge');
        } finally {
            setSaving('');
        }
    };

    const toggleChargeActive = async (charge: any) => {
        setSaving(`charge-${charge.id}`);
        try {
            const res = await SuperAdminApis.updateBillingCharge(charge.id, { is_active: !charge.is_active });
            if (res?.success) {
                toast.success(charge.is_active ? 'Charge switched off' : 'Charge switched on');
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not update charge');
            }
        } catch (e) {
            toast.error('Could not update charge');
        } finally {
            setSaving('');
        }
    };

    const deleteCharge = async (charge: any) => {
        if (!window.confirm(
            `Delete "${charge.name}"? Invoices already generated keep what they were billed.\n\n`
            + `If you only want to stop charging it from now on, switch it off instead — `
            + `deleting a flat charge and re-creating it can charge a docket twice.`
        )) return;

        setSaving(`charge-${charge.id}`);
        try {
            const res = await SuperAdminApis.deleteBillingCharge(charge.id);
            if (res?.success) {
                toast.success(res?.message || 'Charge deleted');
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not delete charge');
            }
        } catch (e) {
            toast.error('Could not delete charge');
        } finally {
            setSaving('');
        }
    };

    const openStateModal = (zone: any) => {
        setStateModalZone(zone);
        setStateSelection((zone.states || []).map((s: any) => s.state_name));
    };

    const toggleState = (state: string) => {
        setStateSelection((prev) =>
            prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state]
        );
    };

    const saveStates = async () => {
        setSaving('states');
        try {
            const res = await SuperAdminApis.setBillingZoneStates(stateModalZone.id, stateSelection);
            if (res?.success) {
                const moved = res.data?.moved_from_others || [];
                toast.success('States updated');
                // Reassigning from a sibling zone is allowed and silent otherwise —
                // say so, or a rate change looks like it did nothing.
                if (moved.length) {
                    toast.info(`Moved ${moved.map((m: any) => `${m.state} from ${m.from}`).join(', ')}`);
                }
                setStateModalZone(null);
                loadConfig(clientId);
            } else {
                toast.warn(res?.message || 'Could not update states');
            }
        } catch (e) {
            toast.error('Could not update states');
        } finally {
            setSaving('');
        }
    };

    /** Which other zone currently holds a state, so the picker can say so. */
    const zoneHolding = (state: string) => {
        if (!config?.zones) return null;
        const found = config.zones.find(
            (z: any) => z.id !== stateModalZone?.id
                && (z.states || []).some((s: any) => s.state_name.trim().toUpperCase() === state.trim().toUpperCase())
        );
        return found ? found.name : null;
    };

    const unassigned: string[] = config?.unassigned_states || [];
    const zonesWithoutRate: string[] = config?.zones_without_rate || [];

    return (
        <>
            <AdminSidebar title="Billing Zones & Rates" />
            <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen">
                <AdminNavbar title="Billing Zones & Rates" />

                <div className="lg:px-10 px-6 pb-10 pt-4">

                    {/* Client selector + management */}
                    <div className="bg-white rounded-lg shadow p-4 mb-6">
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="w-full max-w-xs">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Client</label>
                                <select
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                >
                                    {clients.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            <button onClick={() => openClientForm(config?.client)}
                                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                                <Pencil size={14} /> Edit client
                            </button>
                            <button onClick={() => openClientForm(null)}
                                className="bg-gray-800 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                                <Plus size={14} /> New client
                            </button>
                            {config?.client && (
                                <button onClick={deleteClient} disabled={saving === 'delete-client'}
                                    title={`Delete ${config.client.name}`}
                                    className="bg-white text-[#E04343] border border-[#E04343]/40 px-3 py-2 rounded-md text-sm flex items-center gap-1.5 hover:bg-red-50 disabled:opacity-50">
                                    <Trash2 size={14} /> {saving === 'delete-client' ? 'Deleting…' : 'Delete'}
                                </button>
                            )}
                        </div>

                        {config?.client && (
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 mt-3 pt-3 border-t">
                                <span>
                                    {config.client.match_type === 'account_code' ? (
                                        <>Matches account code
                                            <span className="font-semibold text-gray-800"> {config.client.sender_name_match}</span>
                                        </>
                                    ) : (
                                        <>Matches sender name containing
                                            <span className="font-semibold text-gray-800"> “{config.client.sender_name_match}”</span>
                                        </>
                                    )}
                                </span>
                                <span>
                                    Billed in <span className="font-semibold text-gray-800">{config.client.currency} ({symbol})</span>
                                </span>
                                <span>
                                    VAT{' '}
                                    {config.client.vat_enabled
                                        ? <span className="font-semibold text-gray-800">on · {Number(config.client.vat_rate)}%</span>
                                        : <span className="font-semibold text-gray-500">off</span>}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r">
                        <p className="font-semibold text-blue-900 flex items-center gap-2">
                            <Info size={16} /> How these rates are applied
                        </p>
                        <p className="text-sm text-blue-800 mt-2">
                            An invoice prices each shipment from its <strong>destination state</strong>: the state
                            decides the zone, the zone decides the price. Each zone reads as
                            <strong> 0 to X kg = Y</strong>, then <strong>each additional B kg = Z</strong>.
                            Part of a block counts as a whole block.
                        </p>
                        <p className="text-sm text-blue-800 mt-2">
                            <strong>Example</strong> — 0–5kg = {symbol}2.00, then each additional 1kg = {symbol}0.50:
                            a 3kg shipment bills {symbol}2.00, 5kg bills {symbol}2.00, 5.1kg bills {symbol}2.50
                            (one whole extra kg), 6kg bills {symbol}2.50, 6.1kg bills {symbol}3.00.
                        </p>
                        <p className="text-sm text-blue-800 mt-2">
                            These zones are separate from the ones under Manage Rates and affect nothing else.
                        </p>
                    </div>

                    {/* Gaps that would silently cost money */}
                    {unassigned.length > 0 && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 rounded-r">
                            <p className="font-semibold text-amber-900 flex items-center gap-2">
                                <AlertTriangle size={16} /> {unassigned.length} state(s) are not in any zone
                            </p>
                            <p className="text-sm text-amber-800 mt-1">
                                Shipments delivered to these states cannot be priced and will be listed as unmapped
                                when you generate an invoice, not billed.
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {unassigned.map((s) => (
                                    <span key={s} className="bg-amber-100 text-amber-900 px-2 py-1 rounded text-xs font-medium">{s}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {zonesWithoutRate.length > 0 && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-r">
                            <p className="font-semibold text-red-900 flex items-center gap-2">
                                <AlertTriangle size={16} /> No rate set for: {zonesWithoutRate.join(', ')}
                            </p>
                            <p className="text-sm text-red-800 mt-1">
                                Invoice generation is blocked until every zone in use has a rate.
                            </p>
                        </div>
                    )}

                    {loader ? (
                        <div className="flex justify-center py-20">
                            <Oval height={50} width={50} color="#E04343" secondaryColor="#FCBF94" />
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-[17px] font-semibold text-[#1C1C1C]">
                                    Zones ({config?.zones?.length || 0})
                                </h3>
                                <button
                                    onClick={() => setShowZoneForm(!showZoneForm)}
                                    className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2"
                                >
                                    <Plus size={16} /> Add Zone
                                </button>
                            </div>

                            {showZoneForm && (
                                <div className="bg-white rounded-lg shadow p-4 mb-5">
                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                        <input className="px-3 py-2 border rounded-md text-sm" placeholder="Zone name (e.g. Zone 5)"
                                            value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} />
                                        <input className="px-3 py-2 border rounded-md text-sm" placeholder="Description"
                                            value={zoneForm.description} onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })} />
                                        <input type="number" step="0.01" className="px-3 py-2 border rounded-md text-sm" placeholder="Covers 0 – ? kg"
                                            value={zoneForm.base_weight_kg} onChange={(e) => setZoneForm({ ...zoneForm, base_weight_kg: e.target.value })} />
                                        <input type="number" step="0.0001" className="px-3 py-2 border rounded-md text-sm" placeholder={`for ${symbol}?`}
                                            value={zoneForm.base_amount} onChange={(e) => setZoneForm({ ...zoneForm, base_amount: e.target.value })} />
                                        <input type="number" step="0.01" className="px-3 py-2 border rounded-md text-sm" placeholder="then each ? kg"
                                            value={zoneForm.additional_block_kg} onChange={(e) => setZoneForm({ ...zoneForm, additional_block_kg: e.target.value })} />
                                        <input type="number" step="0.0001" className="px-3 py-2 border rounded-md text-sm" placeholder={`costs ${symbol}?`}
                                            value={zoneForm.additional_per_kg} onChange={(e) => setZoneForm({ ...zoneForm, additional_per_kg: e.target.value })} />
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <button onClick={createZone} disabled={saving === 'zone-new'}
                                            className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm disabled:opacity-60">
                                            {saving === 'zone-new' ? 'Creating…' : 'Create Zone'}
                                        </button>
                                        <button onClick={() => { setShowZoneForm(false); setZoneForm(emptyZoneForm); }}
                                            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm">Cancel</button>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {(config?.zones || []).map((zone: any) => {
                                    const edit = rateEdits[zone.id] || {};
                                    return (
                                        <div key={zone.id} className="bg-white rounded-lg shadow p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div>
                                                    <h4 className="text-lg font-semibold text-[#E04343] flex items-center gap-2">
                                                        <Layers size={18} /> {zone.name}
                                                    </h4>
                                                    <p className="text-sm text-gray-600">{zone.description || '—'}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-[#E04343] text-white px-3 py-1 rounded-full text-xs">
                                                        {zone.state_count} states
                                                    </span>
                                                    <button onClick={() => deleteZone(zone)} disabled={saving === `del-${zone.id}`}
                                                        className="text-gray-400 hover:text-red-600" title="Delete zone">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>

                                            {!zone.has_rate && (
                                                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-3">
                                                    No rate configured — invoices cannot be generated while this zone is in use.
                                                </div>
                                            )}

                                            {/* Reads as the agreement does:
                                                0 to X kg = Y, then each additional B kg = Z */}
                                            <div className="grid grid-cols-2 gap-2 mb-1">
                                                <div>
                                                    <label className="block text-[11px] text-gray-500 mb-1">Covers 0 – this weight (kg)</label>
                                                    <input type="number" step="0.01" className="w-full px-2 py-1.5 border rounded text-sm"
                                                        value={edit.base_weight_kg ?? ''}
                                                        onChange={(e) => setRateEdits({ ...rateEdits, [zone.id]: { ...edit, base_weight_kg: e.target.value } })} />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] text-gray-500 mb-1">…for this price ({symbol})</label>
                                                    <input type="number" step="0.0001" className="w-full px-2 py-1.5 border rounded text-sm"
                                                        value={edit.base_amount ?? ''}
                                                        onChange={(e) => setRateEdits({ ...rateEdits, [zone.id]: { ...edit, base_amount: e.target.value } })} />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 mb-2">
                                                <div>
                                                    <label className="block text-[11px] text-gray-500 mb-1">Then each additional (kg)</label>
                                                    <input type="number" step="0.01" className="w-full px-2 py-1.5 border rounded text-sm"
                                                        value={edit.additional_block_kg ?? ''}
                                                        onChange={(e) => setRateEdits({ ...rateEdits, [zone.id]: { ...edit, additional_block_kg: e.target.value } })} />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] text-gray-500 mb-1">…costs ({symbol})</label>
                                                    <input type="number" step="0.0001" className="w-full px-2 py-1.5 border rounded text-sm"
                                                        value={edit.additional_per_kg ?? ''}
                                                        onChange={(e) => setRateEdits({ ...rateEdits, [zone.id]: { ...edit, additional_per_kg: e.target.value } })} />
                                                </div>
                                            </div>

                                            {/* Worked example straight off the numbers above, so the
                                                rounding is visible before anything is saved. */}
                                            {(() => {
                                                const base = Number(edit.base_amount || 0);
                                                const limit = Number(edit.base_weight_kg || 0);
                                                const block = Number(edit.additional_block_kg || 1);
                                                const amount = Number(edit.additional_per_kg || 0);
                                                const b = block > 0 ? block : 1;
                                                const samples = [limit, Number((limit + b * 0.1).toFixed(2)), limit + b, Number((limit + b * 1.1).toFixed(2))];

                                                return (
                                                    <div className="bg-gray-50 rounded px-2.5 py-2 mb-3">
                                                        <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Worked example</p>
                                                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-600">
                                                            {samples.map((w, i) => {
                                                                const r = priceFor(w, base, limit, block, amount);
                                                                return (
                                                                    <span key={i}>
                                                                        {trimKg(w)}kg ={' '}
                                                                        <span className="font-semibold text-gray-800">{formatRate(r.total, symbol)}</span>
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            <div className="flex gap-2">
                                                <button onClick={() => saveRate(zone)} disabled={saving === `rate-${zone.id}`}
                                                    className="bg-[#E04343] text-white px-3 py-1.5 rounded text-xs flex items-center gap-1 disabled:opacity-60">
                                                    <Save size={14} /> {saving === `rate-${zone.id}` ? 'Saving…' : 'Save rate'}
                                                </button>
                                                <button onClick={() => openStateModal(zone)}
                                                    className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded text-xs flex items-center gap-1">
                                                    <MapPin size={14} /> Manage states
                                                </button>
                                            </div>

                                            <div className="border-t mt-3 pt-3">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {(zone.states || []).length === 0 && (
                                                        <span className="text-xs text-gray-400">No states assigned</span>
                                                    )}
                                                    {(zone.states || []).map((s: any) => (
                                                        <span key={s.id} className="bg-gray-100 px-2 py-0.5 rounded text-[11px]">{s.state_name}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ── Docket-level charges ─────────────────────────────
                                Deliberately below the zones and visually separated:
                                these are NOT zone rates. A zone prices one shipment
                                from its destination state (last mile). These price a
                                whole docket, which carries shipments bound for every
                                zone at once. */}
                            <div className="mt-8 border-t pt-6">
                                <div className="flex items-start justify-between gap-4 mb-1">
                                    <div>
                                        <h2 className="text-lg font-semibold flex items-center gap-2">
                                            <Package size={18} className="text-[#E04343]" />
                                            Additional charges ({(config?.charges || []).length})
                                        </h2>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            Charged per <span className="font-medium">docket</span>, on top of the zone rates above.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => { setChargeForm({ ...emptyChargeForm }); setShowChargeForm(true); }}
                                        className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 shrink-0"
                                    >
                                        <Plus size={16} /> Add charge
                                    </button>
                                </div>

                                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-md px-4 py-3 my-4">
                                    <p className="text-sm text-amber-900 font-semibold flex items-center gap-2 mb-1">
                                        <Info size={15} /> How these are applied
                                    </p>
                                    <p className="text-sm text-amber-900">
                                        The zone rates above price <strong>last-mile delivery</strong>, one shipment at
                                        a time. Charges here price the <strong>docket</strong> — the consignment as a
                                        whole. When an invoice runs, the shipments are grouped by their docket number
                                        and each charge is applied once per docket:
                                    </p>
                                    <ul className="text-sm text-amber-900 mt-2 space-y-1 list-disc list-inside">
                                        <li>
                                            <strong>Per kg</strong> — the docket's total weight × the rate. A docket
                                            of 412.5kg at {symbol}0.08 = {formatMoney(33, symbol)}.
                                        </li>
                                        <li>
                                            <strong>Flat</strong> — the rate once, whatever the docket weighs. 5 dockets
                                            at {formatMoney(5, symbol)} = {formatMoney(25, symbol)}.
                                        </li>
                                    </ul>
                                    <p className="text-sm text-amber-900 mt-2">
                                        A flat charge is only ever taken <strong>once per docket</strong>. If half a
                                        docket bills this week and the rest next week, it is not charged again.
                                    </p>
                                </div>

                                {(config?.charges || []).length === 0 ? (
                                    <div className="border border-dashed rounded-lg py-10 text-center">
                                        <Package size={28} className="mx-auto text-gray-300 mb-2" />
                                        <p className="text-sm text-gray-500">No additional charges.</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Invoices for this client bill last-mile delivery only.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="border rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide">
                                                <tr>
                                                    <th className="text-left px-4 py-2.5 font-medium">Charge</th>
                                                    <th className="text-left px-4 py-2.5 font-medium">Basis</th>
                                                    <th className="text-right px-4 py-2.5 font-medium">Rate</th>
                                                    <th className="text-left px-4 py-2.5 font-medium">Example</th>
                                                    <th className="text-center px-4 py-2.5 font-medium">Active</th>
                                                    <th className="px-4 py-2.5" />
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {(config?.charges || []).map((charge: any) => {
                                                    const flat = charge.charge_type === 'flat';
                                                    const range = charge.charge_type === 'range';

                                                    // Same rule the invoice applies: part of a block is a whole block.
                                                    const rangeAmount = (w: number) => {
                                                        const block = Math.max(0.01, Number(charge.additional_block_kg || 1));
                                                        const blocks = Math.ceil(Math.max(0, w - Number(charge.base_weight_kg || 0)) / block - 1e-9);
                                                        return Number(charge.base_amount || 0) + Math.max(0, blocks) * Number(charge.rate);
                                                    };
                                                    return (
                                                        <tr key={charge.id} className={charge.is_active ? '' : 'opacity-50'}>
                                                            <td className="px-4 py-3">
                                                                <p className="font-medium text-gray-900">{charge.name}</p>
                                                                {charge.description && (
                                                                    <p className="text-xs text-gray-500">{charge.description}</p>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                                                    range ? 'bg-amber-50 text-amber-700'
                                                                        : flat ? 'bg-indigo-50 text-indigo-700'
                                                                        : 'bg-emerald-50 text-emerald-700'}`}>
                                                                    {range ? <Layers size={12} /> : flat ? <Package size={12} /> : <Scale size={12} />}
                                                                    {range ? 'Range, per docket' : flat ? 'Flat, per docket' : 'Per kg of docket weight'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-semibold tabular-nums">
                                                                {range ? (
                                                                    <>
                                                                        {formatMoney(Number(charge.base_amount || 0), symbol)}
                                                                        <span className="text-gray-400 font-normal text-xs">
                                                                            {' '}0–{Number(charge.base_weight_kg || 0)}kg
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {formatRate(charge.rate, symbol)}
                                                                        <span className="text-gray-400 font-normal text-xs">
                                                                            {flat ? ' /docket' : ' /kg'}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-xs text-gray-500">
                                                                {range
                                                                    ? `then ${formatRate(charge.rate, symbol)} per ${Number(charge.additional_block_kg || 1)}kg · a 400kg docket = ${formatMoney(rangeAmount(400), symbol)}`
                                                                    : flat
                                                                        ? `5 dockets = ${formatMoney(Number(charge.rate) * 5, symbol)}`
                                                                        : `a 400kg docket = ${formatMoney(Number(charge.rate) * 400, symbol)}`}
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                <button
                                                                    onClick={() => toggleChargeActive(charge)}
                                                                    disabled={saving === `charge-${charge.id}`}
                                                                    title={charge.is_active ? 'Switch off — stops charging from now on' : 'Switch on'}
                                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:opacity-50 ${charge.is_active ? 'bg-[#E04343]' : 'bg-gray-300'}`}
                                                                >
                                                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${charge.is_active ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                                                                </button>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button
                                                                        onClick={() => {
                                                                            setChargeForm({
                                                                                id: charge.id,
                                                                                name: charge.name,
                                                                                charge_type: charge.charge_type,
                                                                                base_amount: charge.base_amount ?? '',
                                                                                base_weight_kg: charge.base_weight_kg ?? '',
                                                                                additional_block_kg: charge.additional_block_kg ?? 1,
                                                                                rate: String(charge.rate ?? ''),
                                                                                description: charge.description || '',
                                                                            });
                                                                            setShowChargeForm(true);
                                                                        }}
                                                                        className="p-1.5 text-gray-400 hover:text-gray-700"
                                                                        title="Edit"
                                                                    >
                                                                        <Pencil size={15} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => deleteCharge(charge)}
                                                                        disabled={saving === `charge-${charge.id}`}
                                                                        className="p-1.5 text-gray-400 hover:text-[#E04343] disabled:opacity-50"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 size={15} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            {/* ── Return charges ───────────────────────────────
                                The third thing this page prices, and the only one
                                that is not about delivering forwards: what the client
                                pays to have a shipment sent back. One per client, so
                                this is a single card and not a list. */}
                            <div className="mt-8 border-t pt-6">
                                <div className="flex items-start justify-between gap-4 mb-1">
                                    <div>
                                        <h2 className="text-lg font-semibold flex items-center gap-2">
                                            <CornerUpLeft size={18} className="text-[#E04343]" />
                                            Return charges
                                        </h2>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            Charged per <span className="font-medium">returned shipment</span>, on a
                                            return invoice. Nothing to do with the zone rates above.
                                        </p>
                                    </div>
                                    {returnRate && (
                                        <span className={`text-[11px] px-2 py-1 rounded shrink-0 ${
                                            returnRate.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                            {returnRate.is_active ? 'Active' : 'Off'}
                                        </span>
                                    )}
                                </div>

                                <div className="bg-purple-50 border-l-4 border-purple-400 rounded-r-md px-4 py-3 my-4">
                                    <p className="text-sm text-purple-900 font-semibold flex items-center gap-2 mb-1">
                                        <Info size={15} /> How a return invoice is priced
                                    </p>
                                    <p className="text-sm text-purple-900">
                                        A return invoice is built from a sheet the client sends — tracking number,
                                        weight, status and customs duty. For every row whose status says
                                        <strong> return</strong>, the weight is priced by the rate below and the
                                        customs duty is added on top. Every other status
                                        (<em>destroyed</em>, <em>DDP shipment</em>, anything else) is billed for its
                                        customs duty alone and carries no return charge.
                                    </p>
                                    <p className="text-sm text-purple-900 mt-2">
                                        Customs duty is passed straight through as the client stated it — never
                                        priced here, and never VAT-ed. A return invoice carries no VAT at all.
                                    </p>
                                </div>

                                <div className="border rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">How it is charged</label>
                                            <select
                                                value={returnForm.charge_type}
                                                onChange={(e) => setReturnForm({ ...returnForm, charge_type: e.target.value })}
                                                className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                            >
                                                <option value="range">Range — a base band, then per block</option>
                                                <option value="per_kg">Per kg</option>
                                                <option value="flat">Flat, per shipment</option>
                                            </select>
                                        </div>

                                        {returnForm.charge_type === 'flat' && (
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">
                                                    Charge per shipment ({symbol})
                                                </label>
                                                <input type="number" step="0.0001" min="0"
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    value={returnForm.rate}
                                                    onChange={(e) => setReturnForm({ ...returnForm, rate: e.target.value })} />
                                            </div>
                                        )}

                                        {returnForm.charge_type === 'per_kg' && (
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">
                                                    Rate per kg ({symbol})
                                                </label>
                                                <input type="number" step="0.0001" min="0"
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    value={returnForm.rate}
                                                    onChange={(e) => setReturnForm({ ...returnForm, rate: e.target.value })} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Laid out to read as the sentence the agreement uses. */}
                                    {returnForm.charge_type === 'range' && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">Covers 0 – this weight (kg)</label>
                                                <input type="number" step="0.01" min="0"
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    value={returnForm.base_weight_kg}
                                                    onChange={(e) => setReturnForm({ ...returnForm, base_weight_kg: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">…for this price ({symbol})</label>
                                                <input type="number" step="0.0001" min="0"
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    value={returnForm.base_amount}
                                                    onChange={(e) => setReturnForm({ ...returnForm, base_amount: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">Then each additional (kg)</label>
                                                <input type="number" step="0.01" min="0.01"
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    value={returnForm.additional_block_kg}
                                                    onChange={(e) => setReturnForm({ ...returnForm, additional_block_kg: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">…costs ({symbol})</label>
                                                <input type="number" step="0.0001" min="0"
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    value={returnForm.rate}
                                                    onChange={(e) => setReturnForm({ ...returnForm, rate: e.target.value })} />
                                            </div>
                                        </div>
                                    )}

                                    {/* The same arithmetic the invoice runs, on the numbers just typed —
                                        so an off-by-one band is visible here rather than on a sent invoice. */}
                                    {(() => {
                                        const base  = Number(returnForm.base_amount) || 0;
                                        const limit = Number(returnForm.base_weight_kg) || 0;
                                        const block = Number(returnForm.additional_block_kg) || 1;
                                        const rate  = Number(returnForm.rate) || 0;

                                        const samples = returnForm.charge_type === 'range'
                                            ? [limit, limit + block / 2, limit + block, limit + block * 2.2]
                                            : [0.5, 1, 2.4, 5];

                                        return (
                                            <div className="bg-gray-50 border rounded-md px-3 py-2 mt-3">
                                                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Worked example</p>
                                                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                                                    {samples.map((w: number, i: number) => {
                                                        const kg = Math.max(0, w);
                                                        const amount = returnForm.charge_type === 'flat'
                                                            ? rate
                                                            : returnForm.charge_type === 'per_kg'
                                                                ? kg * rate
                                                                : priceFor(kg, base, limit, block, rate).total;

                                                        return (
                                                            <span key={i} className="text-gray-600">
                                                                {trimKg(kg)}kg = <strong className="text-gray-900">{formatMoney(amount, symbol)}</strong>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                                <p className="text-[11px] text-gray-400 mt-1">
                                                    Plus that shipment's customs duty, straight off the client's sheet.
                                                </p>
                                            </div>
                                        );
                                    })()}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">
                                                Description <span className="text-gray-400">— optional</span>
                                            </label>
                                            <input type="text"
                                                className="w-full px-3 py-2 border rounded-md text-sm"
                                                placeholder="Shown on the invoice line, e.g. Return to origin"
                                                value={returnForm.description}
                                                onChange={(e) => setReturnForm({ ...returnForm, description: e.target.value })} />
                                        </div>
                                        <div className="flex items-end">
                                            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                                                <input type="checkbox"
                                                    checked={returnForm.is_active}
                                                    onChange={(e) => setReturnForm({ ...returnForm, is_active: e.target.checked })} />
                                                Active
                                                <span className="text-xs text-gray-400">
                                                    — off means this client cannot be return billed at all
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 mt-4">
                                        <button onClick={saveReturnRate} disabled={saving === 'return-rate'}
                                            className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                            <Save size={15} /> {saving === 'return-rate' ? 'Saving…' : 'Save return rate'}
                                        </button>
                                        {!returnRate && (
                                            <span className="text-xs text-gray-500">
                                                {config?.client?.name} has no return rate yet — return invoices for this
                                                client are refused until one is set.
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── Invoice companies ─────────────────────────────
                        Page-level, not per-client: a company is the name an invoice is
                        issued UNDER, while everything above is about who it is issued
                        TO. Any company can be picked for any client's invoice. */}
                    <div className="bg-white rounded-lg shadow p-5 mt-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <Building2 size={18} className="text-[#E04343]" />
                                    Invoice companies ({companies.length})
                                </h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    Trading names an invoice can be issued under instead of Skynet.
                                </p>
                            </div>
                            <button
                                onClick={() => openCompanyForm(null)}
                                className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 shrink-0"
                            >
                                <Plus size={16} /> Add company
                            </button>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-md px-4 py-3 my-4">
                            <p className="text-sm text-blue-900 font-semibold flex items-center gap-2 mb-1">
                                <Info size={15} /> How these are used
                            </p>
                            <p className="text-sm text-blue-900">
                                When you generate an invoice you can pick one of these companies, and its name
                                and logo appear as the sender on the PDF and the Excel in place of Skynet's.
                                Pick nothing and the invoice is issued as{' '}
                                <strong>Skynet Worldwide Express Nigeria</strong>, exactly as before.
                            </p>
                            <p className="text-sm text-blue-900 mt-2">
                                Address, phone, email and website are optional and each falls back to Skynet's on
                                its own — but an invoice carrying one company's name above another's address is
                                worse than not overriding it at all, so fill in the contact details you have.
                            </p>
                        </div>

                        {companies.length === 0 ? (
                            <div className="border border-dashed rounded-lg py-10 text-center">
                                <Building2 size={28} className="mx-auto text-gray-300 mb-2" />
                                <p className="text-sm text-gray-500">No companies added.</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Every invoice is issued as Skynet Worldwide Express Nigeria.
                                </p>
                            </div>
                        ) : (
                            <div className="border rounded-lg overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                                        <tr>
                                            <th className="px-3 py-2 font-semibold">Logo</th>
                                            <th className="px-3 py-2 font-semibold">Company</th>
                                            <th className="px-3 py-2 font-semibold">Contact</th>
                                            <th className="px-3 py-2 font-semibold">Status</th>
                                            <th className="px-3 py-2 font-semibold text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {companies.map((c: any) => (
                                            <tr key={c.id} className={c.is_active ? '' : 'bg-gray-50/60'}>
                                                <td className="px-3 py-2">
                                                    {c.logo ? (
                                                        <img src={c.logo} alt={c.name}
                                                            className="h-9 w-auto max-w-[110px] object-contain" />
                                                    ) : (
                                                        <span className="flex items-center gap-1.5 text-xs text-gray-400">
                                                            <ImageIcon size={14} /> none
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium text-gray-800">{c.name}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {c.address || <span className="text-gray-400">Skynet's address</span>}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-xs text-gray-600">
                                                    <div>{c.phone || <span className="text-gray-400">Skynet's phone</span>}</div>
                                                    <div>{c.email || c.website || ''}</div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className={`text-[11px] px-2 py-0.5 rounded ${
                                                        c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                                        {c.is_active ? 'Selectable' : 'Off'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <button
                                                            onClick={() => toggleCompany(c)}
                                                            disabled={saving === `company-${c.id}`}
                                                            className="px-2 py-1 text-xs border rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                                            title={c.is_active
                                                                ? 'Stop this company appearing when generating invoices'
                                                                : 'Let this company be picked when generating invoices'}
                                                        >
                                                            {c.is_active ? 'Switch off' : 'Switch on'}
                                                        </button>
                                                        <button
                                                            onClick={() => openCompanyForm(c)}
                                                            className="p-1.5 text-gray-400 hover:text-gray-700"
                                                            title="Edit"
                                                        >
                                                            <Pencil size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteCompany(c)}
                                                            disabled={saving === `company-${c.id}`}
                                                            className="p-1.5 text-gray-400 hover:text-[#E04343] disabled:opacity-50"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add / edit an invoice company */}
            {companyForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">
                                {companyForm.id ? `Edit ${companyForm.name || 'company'}` : 'New invoice company'}
                            </h3>
                            <button onClick={() => { setCompanyForm(null); setLogoFile(null); setLogoPreview(''); }}
                                className="text-gray-400 hover:text-gray-700">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Company name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    value={companyForm.name}
                                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                                    placeholder="e.g. Skynet Logistics Ghana Ltd"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Printed as the sender at the top of the invoice.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Logo</label>
                                <div className="flex items-center gap-3">
                                    <div className="h-16 w-32 border rounded-md flex items-center justify-center bg-gray-50 shrink-0 overflow-hidden">
                                        {logoPreview
                                            ? <img src={logoPreview} alt="Logo" className="max-h-14 max-w-[120px] object-contain" />
                                            : <ImageIcon size={20} className="text-gray-300" />}
                                    </div>
                                    <div>
                                        <label className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                            <Upload size={14} /> {logoPreview ? 'Replace logo' : 'Choose image'}
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg"
                                                className="hidden"
                                                onChange={(e) => pickLogo(e.target.files?.[0] || null)}
                                            />
                                        </label>
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            PNG or JPEG, up to {MAX_LOGO_KB}KB. No logo means Skynet's is used.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">
                                        Address <span className="text-gray-400">— optional</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        value={companyForm.address}
                                        onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                                        placeholder="Leave blank to keep Skynet's address"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">
                                            Phone <span className="text-gray-400">— optional</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-md text-sm"
                                            value={companyForm.phone}
                                            onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">
                                            Email <span className="text-gray-400">— optional</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-md text-sm"
                                            value={companyForm.email}
                                            onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">
                                        Website <span className="text-gray-400">— optional</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        value={companyForm.website}
                                        onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                                    />
                                </div>
                            </div>

                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={companyForm.is_active}
                                    onChange={(e) => setCompanyForm({ ...companyForm, is_active: e.target.checked })}
                                />
                                Selectable when generating an invoice
                            </label>

                            {companyForm.id && (
                                <p className="text-[11px] text-gray-500 bg-gray-50 border rounded-md px-3 py-2">
                                    Invoices read their letterhead from this record when they are opened, so
                                    correcting an address here also corrects it on invoices already issued under
                                    this name. Changing the <strong>name</strong> changes theirs too.
                                </p>
                            )}
                        </div>

                        <div className="px-5 py-3 border-t flex justify-end gap-2">
                            <button onClick={() => { setCompanyForm(null); setLogoFile(null); setLogoPreview(''); }}
                                className="px-4 py-2 text-sm text-gray-600">
                                Cancel
                            </button>
                            <button onClick={saveCompany} disabled={saving === 'company'}
                                className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                <Save size={15} /> {saving === 'company' ? 'Saving…' : 'Save company'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add / edit a docket-level charge */}
            {showChargeForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">
                                {chargeForm.id ? `Edit ${chargeForm.name}` : 'New additional charge'}
                            </h3>
                            <button onClick={() => setShowChargeForm(false)} className="text-gray-400 hover:text-gray-700">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Charge name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    placeholder="Clearance"
                                    value={chargeForm.name}
                                    onChange={(e) => setChargeForm({ ...chargeForm, name: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-500 mb-2">How is it charged?</label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    {[
                                        { value: 'per_kg', title: 'Per kg', blurb: "The docket's total weight × the rate", icon: <Scale size={16} /> },
                                        { value: 'flat', title: 'Flat', blurb: 'The rate once per docket, whatever it weighs', icon: <Package size={16} /> },
                                        { value: 'range', title: 'Range', blurb: 'A base band, then a rate per further block', icon: <Layers size={16} /> },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setChargeForm({ ...chargeForm, charge_type: opt.value })}
                                            className={`text-left px-3 py-2.5 rounded-md border transition ${chargeForm.charge_type === opt.value
                                                ? 'border-[#E04343] bg-red-50'
                                                : 'border-gray-200 hover:border-gray-300'}`}
                                        >
                                            <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                                                {opt.icon} {opt.title}
                                            </span>
                                            <span className="block text-[11px] text-gray-500 mt-0.5">{opt.blurb}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* The band. Laid out to read as the sentence it represents:
                                0 to X kg costs Y, then each further B kg costs Z. */}
                            {chargeForm.charge_type === 'range' && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Covers 0 – this weight (kg)</label>
                                        <input type="number" step="0.01" className="w-full px-3 py-2 border rounded-md text-sm"
                                            placeholder="100"
                                            value={chargeForm.base_weight_kg}
                                            onChange={(e) => setChargeForm({ ...chargeForm, base_weight_kg: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">…for this price ({symbol})</label>
                                        <input type="number" step="0.0001" className="w-full px-3 py-2 border rounded-md text-sm"
                                            placeholder="50.00"
                                            value={chargeForm.base_amount}
                                            onChange={(e) => setChargeForm({ ...chargeForm, base_amount: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Then each additional (kg)</label>
                                        <input type="number" step="0.01" className="w-full px-3 py-2 border rounded-md text-sm"
                                            placeholder="50"
                                            value={chargeForm.additional_block_kg}
                                            onChange={(e) => setChargeForm({ ...chargeForm, additional_block_kg: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">…costs ({symbol})</label>
                                        <input type="number" step="0.0001" className="w-full px-3 py-2 border rounded-md text-sm"
                                            placeholder="10.00"
                                            value={chargeForm.rate}
                                            onChange={(e) => setChargeForm({ ...chargeForm, rate: e.target.value })} />
                                    </div>
                                </div>
                            )}

                            <div className={chargeForm.charge_type === 'range' ? 'hidden' : ''}>
                                <label className="block text-xs text-gray-500 mb-1">
                                    Rate ({symbol}{chargeForm.charge_type === 'flat' ? ' per docket' : ' per kg'})
                                </label>
                                <input
                                    type="number"
                                    step="0.0001"
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    placeholder="0.0000"
                                    value={chargeForm.rate}
                                    onChange={(e) => setChargeForm({ ...chargeForm, rate: e.target.value })}
                                />
                                {/* Same working the invoice will show, from the number just typed. */}
                                {chargeForm.rate !== '' && !isNaN(Number(chargeForm.rate)) && (
                                    <p className="text-xs text-gray-500 mt-1.5">
                                        {chargeForm.charge_type === 'flat'
                                            ? `5 dockets would be charged ${formatMoney(Number(chargeForm.rate) * 5, symbol)}.`
                                            : `A docket weighing 400kg would be charged ${formatMoney(Number(chargeForm.rate) * 400, symbol)}.`}
                                    </p>
                                )}
                            </div>

                            {/* The same arithmetic the invoice runs, on the numbers just
                                typed — any part of a block counts as a whole one. */}
                            {chargeForm.charge_type === 'range' && chargeForm.rate !== '' && !isNaN(Number(chargeForm.rate)) && (
                                <div className="bg-gray-50 rounded-md px-3 py-2">
                                    <p className="text-[11px] font-semibold text-gray-500 mb-1">Worked example</p>
                                    {[150, 412.5, 1000].map((w) => {
                                        const limit = Number(chargeForm.base_weight_kg || 0);
                                        const block = Math.max(0.01, Number(chargeForm.additional_block_kg || 1));
                                        const base = Number(chargeForm.base_amount || 0);
                                        const blocks = Math.ceil(Math.max(0, w - limit) / block - 1e-9);
                                        const amount = base + Math.max(0, blocks) * Number(chargeForm.rate);
                                        return (
                                            <p key={w} className="text-xs text-gray-600">
                                                A docket of <strong>{w}kg</strong> → {formatMoney(amount, symbol)}
                                                {blocks > 0 && <span className="text-gray-400"> ({blocks} extra block{blocks === 1 ? '' : 's'})</span>}
                                            </p>
                                        );
                                    })}
                                </div>
                            )}

                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Note (optional)</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    placeholder="What this covers"
                                    value={chargeForm.description}
                                    onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
                                />
                            </div>

                            {chargeForm.id && (
                                <p className="text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                                    Invoices already generated keep the rate they were billed at — changing this
                                    only affects invoices generated from now on.
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 px-5 py-3 border-t">
                            <button onClick={() => setShowChargeForm(false)} className="px-4 py-2 text-sm text-gray-600">
                                Cancel
                            </button>
                            <button
                                onClick={saveCharge}
                                disabled={saving === 'charge'}
                                className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60"
                            >
                                <Save size={15} /> {saving === 'charge' ? 'Saving…' : 'Save charge'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* State picker */}
            {stateModalZone && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">States in {stateModalZone.name}</h3>
                            <button onClick={() => setStateModalZone(null)} className="text-gray-400 hover:text-gray-700">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-5 py-3 overflow-y-auto">
                            <p className="text-xs text-gray-500 mb-3">
                                Selecting a state that already belongs to another zone moves it here.
                                A state can only be in one zone.
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {allStates.map((state) => {
                                    const holder = zoneHolding(state);
                                    const checked = stateSelection.includes(state);
                                    return (
                                        <label key={state}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded border text-sm cursor-pointer ${checked ? 'bg-red-50 border-[#E04343]' : 'border-gray-200'}`}>
                                            <input type="checkbox" checked={checked} onChange={() => toggleState(state)} />
                                            <span className="flex-1">{state}</span>
                                            {holder && !checked && (
                                                <span className="text-[10px] text-gray-400">{holder}</span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 px-5 py-3 border-t">
                            <button onClick={() => setStateModalZone(null)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={saveStates} disabled={saving === 'states'}
                                className="bg-[#E04343] text-white px-4 py-2 rounded text-sm disabled:opacity-60">
                                {saving === 'states' ? 'Saving…' : `Save ${stateSelection.length} state(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Client add / edit */}
            {clientForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold flex items-center gap-2">
                                <Building2 size={17} /> {clientForm.id ? 'Edit client' : 'New billing client'}
                            </h3>
                            <button onClick={() => setClientForm(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Client name</label>
                                <input className="w-full px-3 py-2 border rounded-md text-sm" placeholder="e.g. SHEIN"
                                    value={clientForm.name}
                                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Match shipments by</label>

                                {/* Which field identifies this client's shipments. Account code
                                    collects several sender names onto one invoice, which matching
                                    on a name cannot do. */}
                                <div className="flex gap-1 p-1 bg-gray-100 rounded-md mb-2">
                                    {[
                                        ['sender_name', 'Sender name'],
                                        ['account_code', 'Account code'],
                                    ].map(([value, label]) => (
                                        <button key={value} type="button"
                                            onClick={() => setClientForm({ ...clientForm, match_type: value })}
                                            className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition ${
                                                clientForm.match_type === value
                                                    ? 'bg-white text-gray-900 shadow-sm'
                                                    : 'text-gray-500 hover:text-gray-700'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                <input className="w-full px-3 py-2 border rounded-md text-sm"
                                    placeholder={clientForm.match_type === 'account_code' ? 'e.g. ACC-1024' : 'e.g. shein'}
                                    value={clientForm.sender_name_match}
                                    onChange={(e) => setClientForm({ ...clientForm, sender_name_match: e.target.value })} />

                                {clientForm.match_type === 'account_code' ? (
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Shipments whose account code is <strong>exactly</strong> this belong to this
                                        client — so several different sender names sharing one code are billed together.
                                        Matched exactly rather than as a fragment, so ACC-1 never collects ACC-10.
                                    </p>
                                ) : (
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Any shipment whose sender name <strong>contains</strong> this text belongs to this
                                        client. Case does not matter. Keep it short and distinctive — two clients cannot
                                        share the same text.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Billing currency</label>
                                <select className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                                    value={clientForm.currency}
                                    onChange={(e) => setClientForm({ ...clientForm, currency: e.target.value })}>
                                    {Object.keys(currencies).map((code) => (
                                        <option key={code} value={code}>{code} ({currencies[code]})</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-gray-500 mt-1">
                                    Rates and invoices for this client are in this currency. Invoices already generated keep
                                    the currency they were raised in.
                                </p>
                            </div>

                            <div className="border rounded-md p-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={clientForm.vat_enabled}
                                        onChange={(e) => setClientForm({ ...clientForm, vat_enabled: e.target.checked })} />
                                    <span className="text-sm font-semibold text-gray-700">Apply VAT to this client's invoices</span>
                                </label>

                                <div className={`mt-3 ${clientForm.vat_enabled ? '' : 'opacity-40'}`}>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">VAT percentage</label>
                                    <div className="flex items-center gap-2">
                                        <input type="number" step="0.01" min="0" max="100" disabled={!clientForm.vat_enabled}
                                            className="w-32 px-3 py-2 border rounded-md text-sm"
                                            value={clientForm.vat_rate}
                                            onChange={(e) => setClientForm({ ...clientForm, vat_rate: e.target.value })} />
                                        <span className="text-sm text-gray-500">%</span>
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Switching VAT off keeps this percentage saved for when you turn it back on.
                                    </p>
                                </div>
                            </div>

                            {clientForm.id && (
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={clientForm.is_active}
                                        onChange={(e) => setClientForm({ ...clientForm, is_active: e.target.checked })} />
                                    <span className="text-sm text-gray-700">Active</span>
                                </label>
                            )}

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                                <textarea className="w-full px-3 py-2 border rounded-md text-sm" rows={2}
                                    value={clientForm.notes}
                                    onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })} />
                            </div>

                            {!clientForm.id && (
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r text-xs text-blue-800">
                                    After creating the client, add its zones and assign states to them — a client with no
                                    zones cannot price anything.
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 px-5 py-3 border-t">
                            <button onClick={() => setClientForm(null)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={saveClient} disabled={saving === 'client'}
                                className="bg-[#E04343] text-white px-4 py-2 rounded text-sm disabled:opacity-60">
                                {saving === 'client' ? 'Saving…' : (clientForm.id ? 'Save changes' : 'Create client')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={4000} newestOnTop closeOnClick pauseOnHover />
        </>
    );
}

export default BillingZoneSettings;
